import {
  useGetJobsiteConfigFeaturesQuery,
  UserAssignmentType,
} from 'apollo/generated/client-operations';
import { Form } from 'components/form';
import { LoadingError } from 'components/loadingError';
import { JobsiteConfigTabProps } from 'containers/jobsiteConfiguration/types';
import { useJobsiteConfigTabForm } from 'containers/jobsiteConfiguration/helpers/useJobsiteConfigTabForm';
import React from 'react';
import { SiteZonesMode } from 'accessGroups/SiteZonesMode';
import { useFlags } from 'featureFlags';
import {
  type EditJobsiteConfigTabFormData,
  getDefaultValues,
  getDropdownsOptions,
  getFormInputs,
  getJobsiteUpdateInput,
} from './FeaturesTab.forms';

export function FeaturesTab(props: JobsiteConfigTabProps): React.ReactElement {
  const { jobsiteId, tab, onTabApiChange, onIsDirtyChange } = props;

  const { siteLayoutUiEnabled } = useFlags();

  const {
    data: jobsiteConfigData,
    loading: jobsiteConfigLoading,
    error: jobsiteConfigError,
    refetch,
  } = useGetJobsiteConfigFeaturesQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      jobsiteId,
      userUsersInput: {
        jobsiteIds: [jobsiteId],
        userAssignmentType: UserAssignmentType.Jobsite,
      },
    },
  });

  const jobsite = jobsiteConfigData?.getJobsite;
  const userData = jobsiteConfigData?.getCurrentSession.user;

  React.useEffect(
    () =>
      onTabApiChange({
        refetchData: refetch,
        loading: jobsiteConfigLoading,
        jobsiteName: jobsite?.name,
      }),
    [onTabApiChange, refetch, jobsiteConfigLoading, jobsite?.name],
  );

  const { formRef, onSubmit } =
    useJobsiteConfigTabForm<EditJobsiteConfigTabFormData>({
      onTabApiChange,
      onIsDirtyChange,
      tab,
      refetch,
      getInput: (data, dirtyFields) =>
        getJobsiteUpdateInput(jobsite, data, dirtyFields),
    });

  const { formInputs, defaultValues } = React.useMemo(() => {
    const dropdownsOptions = getDropdownsOptions({ userData });
    return {
      formInputs: getFormInputs({ jobsite, ...dropdownsOptions }),
      defaultValues: getDefaultValues({ jobsite, ...dropdownsOptions }),
    };
  }, [userData, jobsite]);

  if (!jobsite) {
    return <LoadingError loading />;
  }

  if (jobsiteConfigError) {
    return <LoadingError error={jobsiteConfigError} />;
  }

  return (
    <div className="odin-relative">
      {jobsiteConfigLoading && (
        <div className="table-backdrop">
          <LoadingError loading />
        </div>
      )}
      {/* With the flag off, a site that is not Off can still be stepped down. */}
      <SiteZonesMode
        key={jobsiteId}
        siteId={jobsiteId}
        stepDownOnly={!siteLayoutUiEnabled}
      />
      <Form
        ref={formRef}
        inputs={formInputs}
        onSubmit={onSubmit}
        onIsDirtyChange={onIsDirtyChange}
        defaultValues={defaultValues}
        className="odin-pr-3"
        inputsContainerClassName="odin-space-y-5 sm:odin-space-y-9 odin-divide-y odin-divide-gray-200 odin-pb-9"
        resetTrigger={jobsite}
      />
    </div>
  );
}
